var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EntityNotesQuery } from 'graphql/EntityNote/queries/EntityNotesQuery';
import { CreateEntityNoteMutation } from 'graphql/EntityNote/mutations/CreateEntityNoteMutation';
import { UpdateEntityNoteMutation } from 'graphql/EntityNote/mutations/UpdateEntityNoteMutation';
import { DeleteEntityNoteMutation } from 'graphql/EntityNote/mutations/DeleteEntityNoteMutation';
import { formatDayLabel, formatFullDateTime, formatTime } from 'helpers/entityReminder';
import { React, useEffect, useMutation, useQuery, useState } from 'libs/react';
import { ActivityComposer } from 'components/activity/ActivityComposer';
import { ActivityMenu } from 'components/activity/ActivityMenu';
import { ActivityText } from 'components/activity/ActivityText';
import { Confirm } from 'components/confirm/Confirm';
import { ActivityEmpty } from 'components/activity/ActivityEmpty';
import { useAlert } from 'components/alert/AlertStack';
import { useModal } from 'components/modal/ModalStack';
import { UserImage } from 'components/user/UserImage';
import { useUser } from 'components/user/UserProvider';
import './EntityNotes.scss';
/**
 * @function groupByDay
 * @since 1.0.0
 * Notes arrive newest first; consecutive notes of the same day share a
 * heading.
 */
function groupByDay(notes) {
    const groups = [];
    for (const note of notes) {
        const label = formatDayLabel(note.createdAt);
        const last = groups[groups.length - 1];
        if (last && last.label === label) {
            last.notes.push(note);
        }
        else {
            groups.push({ label, notes: [note] });
        }
    }
    return groups;
}
/**
 * @function wasEdited
 * @since 1.0.0
 */
function wasEdited(note) {
    return (new Date(note.updatedAt).getTime() - new Date(note.createdAt).getTime() >
        60 * 1000);
}
/**
 * @component EntityNotes
 * @since 1.0.0
 */
export function EntityNotes(props) {
    var _a;
    const { entity, entityId, onCountChange } = props;
    const user = useUser();
    const alert = useAlert();
    const modal = useModal();
    const [editingId, setEditingId] = useState(null);
    const [data, ready, , , refetch] = useQuery(EntityNotesQuery, {
        variables: {
            input: {
                entity,
                entityId
            }
        },
        fetchPolicy: 'network-only',
        skip: !entityId
    });
    const [createNote] = useMutation(CreateEntityNoteMutation);
    const [updateNote] = useMutation(UpdateEntityNoteMutation);
    const [deleteNote] = useMutation(DeleteEntityNoteMutation);
    /**
     * @function onCreate
     * @since 1.0.0
     */
    function onCreate(content) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!entityId)
                return;
            try {
                yield createNote({
                    variables: {
                        input: {
                            entity,
                            entityId,
                            content
                        }
                    }
                });
                refetch();
            }
            catch (error) {
                alert.present({ error });
                throw error;
            }
        });
    }
    /**
     * @function onUpdate
     * @since 1.0.0
     */
    function onUpdate(id, content) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield updateNote({
                    variables: {
                        input: { id, content }
                    }
                });
                setEditingId(null);
                refetch();
            }
            catch (error) {
                alert.present({ error });
                throw error;
            }
        });
    }
    /**
     * @function onDelete
     * @since 1.0.0
     */
    function onDelete(id) {
        function execute() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    yield deleteNote({
                        variables: {
                            input: { id }
                        }
                    });
                    refetch();
                    alert.present({
                        title: t('Note deleted successfully.')
                    });
                }
                catch (error) {
                    alert.present({ error });
                }
            });
        }
        modal.present({
            style: 'dialog',
            title: t('Confirm'),
            picto: 'check',
            modal: (React.createElement(Confirm, { title: t('Delete this note?'), infos: t('This action cannot be undone.'), onAccept: execute }))
        });
    }
    /**
     * @function renderNote
     * @since 1.0.0
     */
    function renderNote(note) {
        var _a, _b, _c;
        const mine = !!note.author && note.author.id === ((_a = user.user) === null || _a === void 0 ? void 0 : _a.id);
        return (React.createElement("li", { key: note.id, className: "entity-notes-item" },
            React.createElement("div", { className: "entity-notes-item-avatar" }, note.author ? (React.createElement(UserImage, { user: note.author })) : (React.createElement("span", { className: "entity-notes-item-avatar-empty" }))),
            React.createElement("div", { className: "entity-notes-item-main" },
                React.createElement("div", { className: "entity-notes-item-header" },
                    React.createElement("span", { className: "entity-notes-item-author" }, (_c = (_b = note.author) === null || _b === void 0 ? void 0 : _b.fullname) !== null && _c !== void 0 ? _c : t('Unknown')),
                    React.createElement("span", { className: "entity-notes-item-date", title: formatFullDateTime(note.createdAt) }, formatTime(note.createdAt)),
                    wasEdited(note) && (React.createElement("span", { className: "entity-notes-item-edited", title: formatFullDateTime(note.updatedAt) }, t('(edited)'))),
                    mine && editingId !== note.id && (React.createElement(ActivityMenu, { className: "entity-notes-item-menu", items: [
                            { value: 'edit', label: t('Edit'), picto: 'fal pen' },
                            {
                                value: 'delete',
                                label: t('Delete'),
                                picto: 'fal trash',
                                danger: true
                            }
                        ], onSelect: (value) => value === 'edit' ? setEditingId(note.id) : onDelete(note.id) }))),
                editingId === note.id ? (React.createElement(ActivityComposer, { placeholder: t('Write a note…'), submitLabel: t('Save'), defaultContent: note.content, onSubmit: (content) => onUpdate(note.id, content), onCancel: () => setEditingId(null) })) : (React.createElement(ActivityText, { value: note.content })))));
    }
    const notes = (_a = data === null || data === void 0 ? void 0 : data.entityNotes) !== null && _a !== void 0 ? _a : [];
    useEffect(() => {
        onCountChange === null || onCountChange === void 0 ? void 0 : onCountChange(notes.length);
    }, [notes.length, onCountChange]);
    return (React.createElement("div", { className: "entity-notes" },
        React.createElement(ActivityComposer, { className: "entity-notes-composer", placeholder: t('Write a note…'), submitLabel: t('Add note'), onSubmit: onCreate }),
        React.createElement("div", { className: "entity-notes-body" }, notes.length === 0 && ready ? (React.createElement(ActivityEmpty, { className: "entity-notes-empty", picto: "fal note-sticky", title: t('No notes yet'), description: t('Keep track of your calls, meetings and decisions here.') })) : (groupByDay(notes).map((group) => (React.createElement("section", { key: group.label, className: "entity-notes-group" },
            React.createElement("h4", { className: "entity-notes-group-label" }, group.label),
            React.createElement("ul", { className: "entity-notes-list" }, group.notes.map(renderNote)))))))));
}
